import React from 'react'
import { Button } from '../button/button.tsx'
import { useNavigate } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { urls } from '../../urls.ts'
import { Icon } from '../icon/icon.tsx'
import { RegisterSrc } from '../../../shared/api/chatApi.ts'
import { SUPPORT_TELEGRAM_URL } from '../../constants.ts'
import { AppMessageKeys } from '../../../shared/translations/messages.ts'

interface InformationBlockProps {
  imageUrl: string
  children?: React.ReactNode
  title: React.ReactNode
  buttonText: React.ReactNode
  buttonId?: string
  returnUrl: string
}

export const InformationBlock = (props: InformationBlockProps) => {
  const navigate = useNavigate()
  return (
    <div className="flex w-full flex-1 items-stretch justify-center bg-white p-16 pb-100 sm:items-center">
      <div className="flex w-full max-w-800 flex-col items-center justify-between rounded sm:p-24 ">
        <img src={props.imageUrl} alt="status-img" className="size-[228px]" />
        <div className="mt-32 whitespace-nowrap text-center text-32 font-extrabold">
          {props.title}
        </div>
        <div className="">
          {props.children ?? (
            <div className="my-16 max-w-400 text-center text-24 font-semibold">
              <FormattedMessage id="pleaseTryAgain" />
            </div>
          )}
        </div>
        <Button
          bg="blue-gradient"
          size="large"
          id={props.buttonId}
          rounded="full"
          className="w-full sm:w-800"
          onClick={() => {
            navigate(props.returnUrl)
          }}
        >
          {props.buttonText}
        </Button>

        <Button
          bg="transparent"
          size="large"
          id="contact-button"
          rounded="full"
          className="mt-8 w-full sm:w-800"
          onClick={() => {
            navigate(urls.contacts)
          }}
        >
          <FormattedMessage id="contacts.title" />
        </Button>
      </div>
    </div>
  )
}

const MOBILE_STEPS: AppMessageKeys[] = [
  'downloadApp',
  'loginWith',
  'enjoyProgress',
]

const BlackDashedLine = () => {
  return (
    <div className="absolute left-14 top-0 z-0 h-full w-4">
      <svg width="3" height="100%" className="absolute left-0 top-0">
        <defs>
          <pattern
            id="black-dotted-pattern"
            width="3"
            height="11"
            patternUnits="userSpaceOnUse"
          >
            <rect width="3" height="8" rx="2" fill="#000000" />
          </pattern>
        </defs>
        <rect width="3" height="100%" fill="url(#black-dotted-pattern)" />
      </svg>
    </div>
  )
}

export const MobileInformationBlock = ({
  mobileContent,
  registerSrc,
  login,
}: {
  mobileContent: React.ReactNode
  registerSrc: RegisterSrc
  login: string
}) => {
  const loginWithText = () => {
    switch (registerSrc) {
      case 'google':
        return <FormattedMessage id="google" />
      case 'apple':
        return <FormattedMessage id="apple" />
      case 'telegram':
        return <FormattedMessage id="telegram" />
      case 'yandex':
        return <FormattedMessage id="yandex" />
      case 'vk':
        return <FormattedMessage id="vk" />
      case 'email':
        return login
      default:
        return registerSrc
    }
  }

  return (
    <div className="flex flex-col items-center justify-center p-40">
      <Icon iconName="checkGreen3" className="size-[160px]" />
      <div className="text-center text-24 font-semibold">
        <p className="mt-40 text-30 font-extrabold">
          <FormattedMessage id="payment.success" />
        </p>
        <div className="mt-24 text-18">{mobileContent}</div>
        <div className="relative mx-auto mb-80 mt-54 flex w-fit flex-col gap-22">
          <BlackDashedLine />
          {MOBILE_STEPS.map((step, index) => (
            <div
              key={step}
              className="z-10 flex items-start justify-start gap-12 text-left text-16 font-semibold"
            >
              <div className="flex size-32 min-w-32 items-center justify-center rounded-full bg-black text-center text-16 font-black text-white ">
                {index + 1}
              </div>
              <span>
                <FormattedMessage
                  id={step}
                  values={
                    step === 'loginWith'
                      ? {
                          regType: <span>{loginWithText()}</span>,
                        }
                      : {}
                  }
                />
              </span>
            </div>
          ))}
        </div>
        <div className="mt-40 text-14 font-medium text-gray6">
          {' '}
          <FormattedMessage
            id="payment.contacts"
            values={{
              contacts: <FormattedMessage id="contacts.title" />,
              telegram: (
                <a className="underline" href={SUPPORT_TELEGRAM_URL}>
                  @uni_ai_ed
                </a>
              ),
            }}
          />
        </div>
      </div>
    </div>
  )
}
